import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useAtomValue, useSetAtom } from "jotai";
import { useMediaQuery } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { freeStockTodayState, userState } from "@/lib/store";
import { isOrtexApp } from "@/lib/utils";
import { getLockedPage, StockRef } from "@/lib/lockedPages";
import { AdvancedOffer, DEFAULT_OFFER, loadAdvancedOffer } from "@/lib/advancedOffer";
import { FreeStock, isFreeToday, loadFreeStocksToday } from "@/lib/freeStocks";
import { trackLockedPanel } from "@/lib/tracking";
import { getLandingUtm } from "@/lib/landingAttribution";
import { usePageTour } from "@/components/tour/usePageTour";
import styles from "./LockedValuePanel.module.scss";

/**
 * Signed-out selling layer for locked data pages.
 *
 * A docked card (bottom-right on desktop, a bottom bar that expands into a
 * sheet on phones) rather than a modal: the visitor can keep reading the page
 * behind it. It says what the page is, what ORTEX Advanced adds on this page,
 * the price with the first-month offer, and two ways in.
 *
 * Shown only to signed-out visitors ("ORTEX Visitor"), and only on the pages
 * listed in `src/lib/lockedPages.ts`. The Advanced pitch is web only, never
 * inside the native apps: store rules forbid linking out to a web checkout
 * there. The free-stock variant below is the exception. Logged-in users, free
 * or paid, never see either.
 *
 * Collapsing is remembered for the browser session, so it does not reopen on
 * every page the visitor moves to.
 *
 * On a stock that is free today (src/lib/freeStocks.ts) the data is unlocked for
 * any signed-in account, so the panel asks the visitor to create a free account
 * or log in instead of selling Advanced. That variant shows in the native apps
 * too (a free account is not a purchase), on the same pages. On every page of a
 * free stock it flags freeStockTodayState, so the padlocks offer the free account.
 */

const COLLAPSED_KEY = "ortex.lockedPanel.collapsed";

const readCollapsed = (fallback: boolean): boolean => {
    try {
        const v = window.sessionStorage.getItem(COLLAPSED_KEY);
        return v === null ? fallback : v === "1";
    } catch {
        return fallback;
    }
};

const writeCollapsed = (value: boolean) => {
    try {
        window.sessionStorage.setItem(COLLAPSED_KEY, value ? "1" : "0");
    } catch {
        /* private mode */
    }
};

const LockedValuePanel = ({ stock }: { stock?: StockRef }) => {
    const { pathname, asPath } = useRouter();
    const user = useAtomValue(userState);
    const isPhone = useMediaQuery("(max-width: 768px)");
    const tour = usePageTour();

    const page = getLockedPage(pathname);
    const isVisitor = user?.subscription_plan === "ORTEX Visitor";
    const visible = !!page && !page.aboutOnly && isVisitor && !isOrtexApp();
    const setFreeStockToday = useSetAtom(freeStockTodayState);

    const [offer, setOffer] = useState<AdvancedOffer>(DEFAULT_OFFER);
    // null until today's free list has loaded. A stock page waits for it, so the first
    // render and the view event already know whether the stock is free.
    const [freeStocks, setFreeStocks] = useState<FreeStock[] | null>(null);
    const freeToday = isFreeToday(stock, freeStocks ?? []);
    const ready = !stock || freeStocks !== null;
    const hasStock = !!stock;
    const freeForVisitor = isVisitor && hasStock && freeToday;
    const showFree = freeForVisitor && !!page;
    const shown = showFree || visible;
    // Phones start as the slim bar (the page stays readable); desktop starts open.
    const [collapsed, setCollapsed] = useState<boolean>(true);

    useEffect(() => {
        if (!visible) return;
        let cancelled = false;
        loadAdvancedOffer().then((o) => {
            if (!cancelled) setOffer(o);
        });
        return () => {
            cancelled = true;
        };
    }, [visible]);

    useEffect(() => {
        if (!isVisitor || !hasStock) return;
        let cancelled = false;
        loadFreeStocksToday().then((f) => {
            if (!cancelled) setFreeStocks(f);
        });
        return () => {
            cancelled = true;
        };
    }, [isVisitor, hasStock]);

    useEffect(() => {
        if (shown) setCollapsed(readCollapsed(isPhone));
    }, [shown, isPhone]);

    useEffect(() => {
        setFreeStockToday(freeForVisitor);
        return () => setFreeStockToday(false);
    }, [freeForVisitor, setFreeStockToday]);

    const trackProps = () => ({
        page: page?.id,
        ticker: stock?.ticker,
        exchange: stock?.exchangesymbol,
        currency: offer.currency,
        offer: offer.offerPrice,
        device: isPhone ? "phone" : "desktop",
        free_stock: freeToday,
        ...getLandingUtm(),
    });

    // One view per page shown (asPath, so moving between tickers counts each).
    const lastViewed = useRef<string>("");
    useEffect(() => {
        if (!shown || !ready || lastViewed.current === asPath) return;
        lastViewed.current = asPath;
        trackLockedPanel("view", { ...trackProps(), collapsed: readCollapsed(isPhone) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shown, ready, asPath]);

    if (!shown || !ready) return null;

    const toggle = () => {
        const next = !collapsed;
        setCollapsed(next);
        writeCollapsed(next);
        trackLockedPanel(next ? "collapse" : "expand", trackProps());
    };

    if (showFree) {
        return <FreeStockPanel stock={stock} collapsed={collapsed} isPhone={isPhone} toggle={toggle} track={(action) => trackLockedPanel(action, trackProps())} />;
    }
    if (!page) return null;

    const offerCta = `Get your first month for ${offer.offerPrice}`;
    // The same offer through Google sign-in: /login?social=google starts the Cognito Google
    // flow at once and carries the promo and checkout flags into the upgrade (AuthPage). Built
    // from the offer link's own parameters, so a change to that link cannot leave this one plain.
    const googleHref = (() => {
        const params = new URLSearchParams(offer.offerHref.split("?")[1] || "");
        params.set("social", "google");
        return `/login?${params.toString()}`;
    })();
    const priceLine = (
        <p className={styles.price}>
            <strong>{offer.offerPrice}</strong> for your first month, then {offer.fullPrice} a month. Cancel anytime.
        </p>
    );

    const ctas = (
        <div className={styles.ctas}>
            {/* A full page load, like the ad links: _app picks the promo up from the URL and
                carries it through sign-up into the checkout (src/lib/checkoutIntent.ts). */}
            <a className={styles.google} href={googleHref} rel="nofollow" onClick={() => trackLockedPanel("first_month_offer_google", trackProps())}>
                <svg aria-hidden viewBox="0 0 48 48" width="18" height="18">
                    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
                    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
                    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
                    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
                </svg>
                Continue with Google
            </a>
            <a className={styles.primary} href={offer.offerHref} rel="nofollow" onClick={() => trackLockedPanel("first_month_offer", trackProps())}>
                {offerCta}
            </a>
            <Link href="/login?register=true" className={styles.secondary} rel="nofollow" onClick={() => trackLockedPanel("start_free", trackProps())}>
                Start free
            </Link>
        </div>
    );

    if (collapsed) {
        return (
            <>
                {isPhone && <div className={styles.phoneSpacer} aria-hidden />}
                <aside className={`${styles.panel} ${styles.collapsed}`} aria-label="Unlock this page with ORTEX Advanced">
                    <button type="button" className={styles.collapsedText} onClick={toggle} aria-expanded={false}>
                        <span className={styles.eyebrow}>ORTEX Advanced</span>
                        <span className={styles.collapsedLine}>
                            Unlock live data · <strong>{offer.offerPrice}</strong> first month
                        </span>
                    </button>
                    <a className={`${styles.primary} ${styles.compact}`} href={offer.offerHref} rel="nofollow" onClick={() => trackLockedPanel("first_month_offer", { ...trackProps(), collapsed: true })}>
                        {isPhone ? "Start" : offerCta}
                    </a>
                    <button type="button" className={styles.iconButton} onClick={toggle} aria-label="Show what Advanced unlocks">
                        <ExpandLessIcon fontSize="small" />
                    </button>
                </aside>
            </>
        );
    }

    return (
        <>
            {isPhone && <div className={styles.phoneSpacer} aria-hidden />}
            {isPhone && <div className={styles.scrim} onClick={toggle} aria-hidden />}
            <aside className={styles.panel} aria-label="Unlock this page with ORTEX Advanced">
                <div className={styles.header}>
                    <span className={styles.eyebrow}>Unlock this page with ORTEX Advanced</span>
                    <button type="button" className={styles.iconButton} onClick={toggle} aria-label="Minimise" aria-expanded>
                        <ExpandMoreIcon fontSize="small" />
                    </button>
                </div>
                <h2 className={styles.headline}>{page.headline(stock)}</h2>
                <p className={styles.sub}>The free view is limited. ORTEX Advanced adds:</p>
                <ul className={styles.unlocks}>
                    {page.unlocks.map((u) => (
                        <li key={u}>
                            <CheckIcon className={styles.check} fontSize="inherit" />
                            <span>{u}</span>
                        </li>
                    ))}
                </ul>
                {page.note && <p className={styles.note}>{page.note}</p>}
                {priceLine}
                {ctas}
                {tour && (
                    <button
                        type="button"
                        className={styles.tourLink}
                        onClick={() => {
                            trackLockedPanel("tour", trackProps());
                            if (isPhone) {
                                setCollapsed(true);
                                writeCollapsed(true);
                            }
                            tour.start();
                        }}
                    >
                        New here? Take the 30-second tour
                    </button>
                )}
            </aside>
        </>
    );
};

/** The free-stock variant: the data is free with an account, so sign up or log in. */
const FreeStockPanel = ({ stock, collapsed, isPhone, toggle, track }: { stock?: StockRef; collapsed: boolean; isPhone: boolean; toggle: () => void; track: (action: string) => void }) => {
    const name = stock ? `${stock.name} (${stock.exchangesymbol}:${stock.ticker})` : "This stock";
    const signUp = (compact = false) => (
        <Link href="/login?register=true" className={`${styles.primary}${compact ? ` ${styles.compact}` : ""}`} rel="nofollow" onClick={() => track("free_stock_signup")}>
            {compact && isPhone ? "Sign up" : "Create free account"}
        </Link>
    );

    if (collapsed) {
        return (
            <>
                {isPhone && <div className={styles.phoneSpacer} aria-hidden />}
                <aside className={`${styles.panel} ${styles.collapsed}`} aria-label="This stock is free with an account">
                    <button type="button" className={styles.collapsedText} onClick={toggle} aria-expanded={false}>
                        <span className={styles.eyebrow}>Free today</span>
                        <span className={styles.collapsedLine}>
                            <strong>{stock?.ticker ?? "This stock"}</strong> is free with an account
                        </span>
                    </button>
                    {signUp(true)}
                    <button type="button" className={styles.iconButton} onClick={toggle} aria-label="Show more">
                        <ExpandLessIcon fontSize="small" />
                    </button>
                </aside>
            </>
        );
    }

    return (
        <>
            {isPhone && <div className={styles.phoneSpacer} aria-hidden />}
            {isPhone && <div className={styles.scrim} onClick={toggle} aria-hidden />}
            <aside className={styles.panel} aria-label="This stock is free with an account">
                <div className={styles.header}>
                    <span className={styles.eyebrow}>Free today with an account</span>
                    <button type="button" className={styles.iconButton} onClick={toggle} aria-label="Minimise" aria-expanded>
                        <ExpandMoreIcon fontSize="small" />
                    </button>
                </div>
                <h2 className={styles.headline}>{name} is free today</h2>
                <p className={styles.sub}>Log in or create a free account to see all the data for this stock. No card needed.</p>
                <div className={styles.ctas}>
                    {signUp()}
                    <Link href="/login" className={styles.secondary} rel="nofollow" onClick={() => track("free_stock_login")}>
                        Log in
                    </Link>
                </div>
            </aside>
        </>
    );
};

export default LockedValuePanel;
